/**
 * The cached Creator collection, read from `src/collection/`.
 *
 * That folder is git-ignored and empty on a fresh clone; `pnpm run
 * cache:collection` fills it (see its README). Everything the experience needs
 * — image targets, artwork images, video — is bundled from there, so the
 * running app never talks to Creator or the CDN.
 *
 * The files are pulled in with `import.meta.glob`, so adding an artwork to the
 * collection and re-running the script is enough; no code change here.
 */

import type { CreatorPayload } from "@core/content/creator/types";
import type { ImageTargetDescriptor } from "@webcore/core/targets/target.image.8w";

/** Shape of `collection.json`, as written by scripts/cache-collection.mjs. */
interface CachedFile {
  type: string;
  file: string;
  bytes: number;
  sha256: string;
  width?: number;
  height?: number;
  sourcePath: string;
}

interface CachedArtwork {
  id: string;
  name: string;
  type: string;
  /**
   * The sign this artwork belongs to, decoded by the cache script from the QR
   * printed on the artwork itself. Absent when the artwork carried no readable
   * QR. This, not the artwork id, is what the app binds on.
   */
  route?: string | null;
  files: Record<string, CachedFile>;
  target?: {
    descriptor: string;
    image: string;
    width: number;
    height: number;
    isRotated: boolean;
  };
}

interface CachedCollection {
  source: string;
  fetchedAt: string;
  id: string;
  type: string;
  name: string;
  artworks: CachedArtwork[];
}

const CACHE_COMMAND = "pnpm run cache:collection";

// Eager so a missing cache is a boot-time failure with a clear message, rather
// than a promise that rejects somewhere inside the AR session.
const manifests = import.meta.glob("../collection/collection.json", {
  eager: true,
  import: "default",
}) as Record<string, CachedCollection>;

const descriptors = import.meta.glob("../collection/*.target.json", {
  eager: true,
  import: "default",
}) as Record<string, ImageTargetDescriptor>;

const targetImages = import.meta.glob("../collection/*.target.jpeg", {
  eager: true,
  query: "?url",
  import: "default",
}) as Record<string, string>;

// Every other cached file (video, artwork image, anything Creator adds later),
// keyed by its on-disk name so the manifest can point straight at it.
const mediaFiles = import.meta.glob("../collection/*.{mp4,mov,jpg,jpeg,png,webp,glb,mp3}", {
  eager: true,
  query: "?url",
  import: "default",
}) as Record<string, string>;

const basename = (p: string): string => p.split("/").pop()!;
const stem = (p: string): string => basename(p).replace(/\.[^.]+$/, "");

/** Bundled asset url for every cached file, keyed by its file name. */
const urlByFileName: Record<string, string> = Object.fromEntries(
  [...Object.entries(mediaFiles), ...Object.entries(targetImages)].map(([p, url]) => [basename(p), url]),
);

const manifest: CachedCollection | null = Object.values(manifests)[0] ?? null;

/** True when `src/collection/` has been populated. */
export const isCollectionCached = (): boolean =>
  manifest !== null && manifest.artworks?.length > 0 && Object.keys(descriptors).length > 0;

/**
 * Fail loudly, and with the fix, when the cache is missing. Called at boot so
 * an unpopulated checkout cannot get as far as a blank camera feed.
 */
export const assertCollectionCached = (): void => {
  if (isCollectionCached()) return;

  const reason = manifest === null
    ? "src/collection/collection.json is missing"
    : `src/collection/ has a manifest but ${manifest.artworks?.length ?? 0} artworks and ` +
      `${Object.keys(descriptors).length} image targets`;

  throw new Error(
    `Cached collection not found — ${reason}.\n` +
      `The app bundles its artwork from src/collection/, which is git-ignored and empty on a fresh clone.\n` +
      `Populate it, then rebuild:\n\n    ${CACHE_COMMAND}\n\n` +
      `See src/collection/README.md.`,
  );
};

/** Where the cache came from and when, for dev logging. */
export const getCacheProvenance = (): { source: string; fetchedAt: string; name: string } | null =>
  manifest && { source: manifest.source, fetchedAt: manifest.fetchedAt, name: manifest.name };

/**
 * Bundled url for a cached file name.
 *
 * Never falls back to the file's Creator url: silently reaching for the CDN is
 * exactly what this cache exists to prevent, and a miss here means the manifest
 * and the folder have drifted apart.
 */
const requireBundledUrl = (fileName: string, context: string): string => {
  const url = urlByFileName[fileName];
  if (url) return url;

  throw new Error(
    `Cached collection is incomplete — ${context} refers to "${fileName}", which is not in src/collection/.\n` +
      `The manifest and the folder have drifted. Repull them together:\n\n    ${CACHE_COMMAND} --force\n`,
  );
};

/**
 * 8th Wall descriptors for the cached targets, with `imagePath` rewritten from
 * the relative name the script wrote to the bundled asset url.
 */
export const getLocalImageTargets = (): ImageTargetDescriptor[] => {
  assertCollectionCached();
  return Object.values(descriptors).map((descriptor) => ({
    ...descriptor,
    imagePath: requireBundledUrl(basename(descriptor.imagePath), `target "${descriptor.name}"`),
  }));
};

/** The cached target for one artwork id, or undefined if it was not cached. */
export const getLocalImageTarget = (artworkId: string): ImageTargetDescriptor | undefined =>
  getLocalImageTargets().find((t) => t.name === artworkId);

/**
 * The cached collection rebuilt as a Creator payload, with every file path
 * pointing at a bundled asset instead of the CDN. Handed to the content store
 * as its injected payload, so nothing is fetched at runtime.
 */
export const getLocalCreatorPayload = (): CreatorPayload => {
  assertCollectionCached();
  const m = manifest!;

  const localPath = (file: CachedFile): string =>
    requireBundledUrl(file.file, `${file.type} file`);

  return {
    id: m.id,
    type: m.type,
    name: m.name,
    artworks: m.artworks.map((artwork) => ({
      id: artwork.id,
      type: artwork.type,
      name: artwork.name,
      files: Object.values(artwork.files).map((file) => ({
        type: file.type,
        path: localPath(file),
        ...(file.width ? { width: file.width, height: file.height } : {}),
        size: file.bytes,
      })),
    })),
  } as unknown as CreatorPayload;
};

/**
 * One cached file for an artwork, by Creator file type ("image-target",
 * "video", ...): its bundled url, and for images the pixel size the cache
 * script measured off the file itself.
 */
export const getLocalFile = (
  artworkId: string,
  fileType: string,
): { url: string; width?: number; height?: number } | undefined => {
  const file = manifest?.artworks.find((a) => a.id === artworkId)?.files[fileType];
  const url = file && urlByFileName[file.file];
  return url ? { url, width: file!.width, height: file!.height } : undefined;
};

/** Ids of every artwork in the cache, in manifest order. */
export const getCachedArtworkIds = (): string[] => manifest?.artworks.map((a) => a.id) ?? [];

/**
 * Sign route -> artwork id, as decoded from the QR on each artwork.
 *
 * Artwork ids are not stable: the collection has already been deleted and
 * rebuilt once, changing every id and every name. Routes are stable, because
 * they are printed on the physical signs. So the app binds a sign to its
 * artwork through this map and holds no artwork id of its own.
 */
export const getArtworkIdsByRoute = (): Record<string, string> =>
  Object.fromEntries(
    (manifest?.artworks ?? [])
      .filter((a) => a.route)
      .map((a) => [a.route as string, a.id]),
  );

/** Artworks in the cache that carry no sign route, for diagnostics. */
export const getUnroutedArtworks = (): { id: string; name: string }[] =>
  (manifest?.artworks ?? [])
    .filter((a) => !a.route)
    .map((a) => ({ id: a.id, name: a.name }));

/** Names of the cached target descriptors, for dev diagnostics. */
export const getCachedTargetNames = (): string[] => Object.keys(descriptors).map(stem);
